import { useRef } from "react";
import { motion } from "framer-motion";
import { CountUp } from "../CountUp";
import { Reveal } from "../Reveal";
import { fmtMoney } from "../../lib/format";
import { trackEvent } from "../../lib/analytics";

const EASE = [0.22, 1, 0.36, 1];

export const SavingsTimeline = ({ calc }) => {
  const fired = useRef({});
  const fire = (key, event, value) => {
    if (fired.current[key]) return;
    fired.current[key] = true;
    trackEvent(event, { value: Math.round(value) });
  };

  const steps = [
    { key: "today", label: "TODAY", value: 0 },
    { key: "6m", label: "6 MONTHS", value: calc.six, event: "savings_6m_viewed" },
    { key: "1y", label: "1 YEAR", value: calc.one, event: "savings_1y_viewed" },
    { key: "2y", label: "2 YEARS", value: calc.two, event: "savings_2y_viewed" },
    { key: "3y", label: "3 YEARS", value: calc.three, event: "savings_3y_viewed", dominant: true },
  ];
  const max = Math.max(calc.three, 1);

  return (
    <div data-testid="savings-timeline" className="mt-24">
      <Reveal className="max-w-2xl">
        <h3 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          Small Processing Fees <span className="text-mint">Add Up Fast.</span>
        </h3>
        <p className="mt-4 text-white/60">
          The difference may look small on each transaction. Over time, it can mean tens of
          thousands of dollars leaving your business unnecessarily.
        </p>
      </Reveal>

      {/* Desktop: animated horizontal growth bars */}
      <div className="mt-12 hidden h-80 items-end gap-4 border-b border-white/10 md:flex">
        {steps.map((s, i) => {
          const pct = Math.max((s.value / max) * 100, 2.5);
          return (
            <motion.div
              key={s.key}
              onViewportEnter={() => s.event && fire(s.key, s.event, s.value)}
              viewport={{ once: true, margin: "-60px" }}
              className="flex h-full flex-1 flex-col items-center justify-end gap-3"
            >
              <CountUp
                value={s.value}
                format={fmtMoney}
                testid={`savings-${s.key}`}
                className={`font-mono ${s.dominant ? "text-3xl font-bold text-mint" : "text-sm text-white/70"}`}
              />
              <motion.div
                animate={{ height: `${pct}%` }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
                className={`w-full max-w-[110px] rounded-t-xl ${
                  s.dominant
                    ? "bg-mint shadow-[0_0_50px_rgba(0,255,136,0.35)]"
                    : "bg-white/10"
                }`}
                style={{ minHeight: 6 }}
              />
              <span className={`font-mono text-[10px] tracking-[0.2em] ${s.dominant ? "text-mint" : "text-white/50"}`}>
                {s.label}
              </span>
            </motion.div>
          );
        })}
      </div>

      {/* Mobile: stacked savings cards */}
      <div className="mt-10 grid grid-cols-2 gap-3 md:hidden">
        {steps.slice(1).map((s) => (
          <motion.div
            key={s.key}
            onViewportEnter={() => s.event && fire(s.key, s.event, s.value)}
            viewport={{ once: true }}
            className={`rounded-2xl border p-5 ${
              s.dominant
                ? "col-span-2 border-mint/40 bg-mint/10"
                : "border-white/10 bg-white/5"
            }`}
            data-testid={`savings-card-${s.key}`}
          >
            <p className="font-mono text-[10px] tracking-[0.25em] text-white/50">{s.label}</p>
            <CountUp
              value={s.value}
              format={fmtMoney}
              className={`mt-2 block font-mono font-bold ${s.dominant ? "text-4xl text-mint" : "text-xl text-white"}`}
            />
            {s.dominant && (
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mint">
                Potential 3-year savings
              </p>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};
